// src/components/FilesTab.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  ClipboardIcon,
  ArrowRightIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { FaFileAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

// In-memory module cache for fast tab-switching during session
const memoryFileCache = new Map(); // key: `${topic}:${filename}` -> code

const getInitialOpenFiles = (topic) => {
  if (!topic) return [];
  try {
    const saved = localStorage.getItem(`codetyper_open_files_${topic}`);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.error("Failed to load open files from localStorage", e);
  }
  return [];
};

const getInitialContentsCache = (topic) => {
  if (!topic) return {};
  try {
    const saved = sessionStorage.getItem(`codetyper_files_cache_${topic}`);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.error("Failed to load files cache from sessionStorage", e);
  }
  return {};
};

const FilesTab = ({ selectedTopic }) => {
  const [files, setFiles] = useState([]);
  const [openFiles, setOpenFiles] = useState(() => getInitialOpenFiles(selectedTopic));
  const [fileContents, setFileContents] = useState(() => getInitialContentsCache(selectedTopic));
  const [fileLoadingMap, setFileLoadingMap] = useState({});
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [copyStatusMap, setCopyStatusMap] = useState({});

  const navigate = useNavigate();
  const backendURL = process.env.REACT_APP_BACKEND_URL;
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // When selectedTopic changes, restore that topic's open files and cache
  useEffect(() => {
    setOpenFiles(getInitialOpenFiles(selectedTopic));
    setFileContents(getInitialContentsCache(selectedTopic));
    setFileLoadingMap({});
  }, [selectedTopic]);

  // Persist openFiles in localStorage per topic
  useEffect(() => {
    if (!selectedTopic) return;
    try {
      localStorage.setItem(
        `codetyper_open_files_${selectedTopic}`,
        JSON.stringify(openFiles)
      );
    } catch (e) {
      console.error("Failed to save open files to localStorage", e);
    }
  }, [openFiles, selectedTopic]);

  // Persist fileContents in sessionStorage per topic
  useEffect(() => {
    if (!selectedTopic) return;
    try {
      sessionStorage.setItem(
        `codetyper_files_cache_${selectedTopic}`,
        JSON.stringify(fileContents)
      );
    } catch (e) {
      console.warn("Storage quota exceeded or error caching file content", e);
    }
  }, [fileContents, selectedTopic]);

  // Listen for file save events from SetterTab / MCQSetter to keep cache updated immediately
  useEffect(() => {
    const handleFileSaved = (e) => {
      const { topic, filename, code } = e.detail || {};
      if (topic === selectedTopic && filename) {
        const memKey = `${topic}:${filename}`;
        memoryFileCache.set(memKey, code);
        setFileContents((prev) => ({ ...prev, [filename]: code }));
      }
    };

    window.addEventListener("codetyper:file_saved", handleFileSaved);
    return () => {
      window.removeEventListener("codetyper:file_saved", handleFileSaved);
    };
  }, [selectedTopic]);

  const fetchFiles = useCallback(async () => {
    if (!selectedTopic) return;
    try {
      setLoading(true);
      setMessage("Fetching files...");
      const response = await axios.post(`${backendURL}/list_files`, {
        topic: selectedTopic,
      });

      if (response.status === 200 && isMountedRef.current) {
        const extractSortableParts = (filename) => {
          const lastDotIndex = filename.lastIndexOf(".");
          const nameWithoutExtension =
            lastDotIndex !== -1 ? filename.slice(0, lastDotIndex) : filename;
          const extension = lastDotIndex !== -1 ? filename.slice(lastDotIndex) : "";

          const match = nameWithoutExtension.match(/^(.*?)(?:_(\d+))?$/);

          return {
            base: match ? match[1] : nameWithoutExtension,
            number: match && match[2] ? parseInt(match[2], 10) : null,
            extension,
          };
        };

        const sortedFiles = [...response.data.files].sort((a, b) => {
          const aParts = extractSortableParts(a);
          const bParts = extractSortableParts(b);

          const baseComparison = bParts.base.localeCompare(aParts.base, undefined, {
            sensitivity: "base",
          });
          if (baseComparison !== 0) {
            return baseComparison;
          }

          if (aParts.number !== null && bParts.number !== null && aParts.number !== bParts.number) {
            return bParts.number - aParts.number;
          }

          if (aParts.number !== null && bParts.number === null) {
            return -1;
          }

          if (aParts.number === null && bParts.number !== null) {
            return 1;
          }

          const extensionComparison = bParts.extension.localeCompare(
            aParts.extension,
            undefined,
            { sensitivity: "base" }
          );
          if (extensionComparison !== 0) {
            return extensionComparison;
          }

          return b.localeCompare(a, undefined, { sensitivity: "base" });
        });

        setFiles(sortedFiles);
        setMessage(
          response.data.files.length
            ? "Files fetched successfully."
            : "No files available."
        );
      } else if (isMountedRef.current) {
        setMessage("Failed to fetch files.");
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error(error);
        setMessage("Error fetching files.");
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [backendURL, selectedTopic]);

  // Load content for a single file (optionally force fresh reload from server)
  const loadContentForFile = useCallback(
    async (file, forceReload = false) => {
      const memKey = `${selectedTopic}:${file}`;

      if (!forceReload) {
        // Check in-memory module cache first
        if (memoryFileCache.has(memKey)) {
          const cachedCode = memoryFileCache.get(memKey);
          setFileContents((prev) => ({ ...prev, [file]: cachedCode }));
          return;
        }

        // Check current state / sessionStorage cache
        if (fileContents[file] !== undefined) {
          return;
        }
      } else {
        // Force reload: bust memory cache for this file
        memoryFileCache.delete(memKey);
      }

      try {
        setFileLoadingMap((prev) => ({ ...prev, [file]: true }));
        const response = await axios.post(`${backendURL}/get_code`, {
          filename: file,
          topic: selectedTopic,
        });

        if (response.status === 200 && isMountedRef.current) {
          const code = response.data.code;
          memoryFileCache.set(memKey, code);
          setFileContents((prev) => ({ ...prev, [file]: code }));
        }
      } catch (error) {
        if (isMountedRef.current) {
          console.error(`Error loading content for ${file}:`, error);
        }
      } finally {
        if (isMountedRef.current) {
          setFileLoadingMap((prev) => ({ ...prev, [file]: false }));
        }
      }
    },
    [backendURL, selectedTopic, fileContents]
  );

  // Reload an individual file explicitly from the server
  const reloadSingleFile = (file) => {
    loadContentForFile(file, true);
  };

  // Full Refresh: busts cache for current topic and re-fetches latest file list & open file contents
  const handleRefresh = async () => {
    // 1. Invalidate topic cache
    for (const key of memoryFileCache.keys()) {
      if (key.startsWith(`${selectedTopic}:`)) {
        memoryFileCache.delete(key);
      }
    }
    try {
      sessionStorage.removeItem(`codetyper_files_cache_${selectedTopic}`);
    } catch (e) {}
    setFileContents({});

    // 2. Fetch fresh file list
    await fetchFiles();

    // 3. Re-fetch fresh contents for all currently open files
    openFiles.forEach((file) => {
      loadContentForFile(file, true);
    });
  };

  // Toggle accordion item (allows multiple open simultaneously)
  const toggleFile = (file) => {
    const isCurrentlyOpen = openFiles.includes(file);

    if (isCurrentlyOpen) {
      // Close this specific file without affecting others
      setOpenFiles((prev) => prev.filter((f) => f !== file));
    } else {
      // Open this specific file
      setOpenFiles((prev) => [...prev, file]);
      // If content isn't cached yet, fetch from server
      const memKey = `${selectedTopic}:${file}`;
      if (!memoryFileCache.has(memKey) && fileContents[file] === undefined) {
        loadContentForFile(file);
      } else if (memoryFileCache.has(memKey) && fileContents[file] === undefined) {
        setFileContents((prev) => ({
          ...prev,
          [file]: memoryFileCache.get(memKey),
        }));
      }
    }
  };

  // If there are already open files on mount/topic change that lack content, fetch them
  useEffect(() => {
    openFiles.forEach((file) => {
      const memKey = `${selectedTopic}:${file}`;
      if (!memoryFileCache.has(memKey) && fileContents[file] === undefined) {
        loadContentForFile(file);
      } else if (memoryFileCache.has(memKey) && fileContents[file] === undefined) {
        setFileContents((prev) => ({
          ...prev,
          [file]: memoryFileCache.get(memKey),
        }));
      }
    });
  }, [openFiles, selectedTopic, fileContents, loadContentForFile]);

  const copyToClipboard = (file, text) => {
    navigator.clipboard.writeText(text || "").then(() => {
      setCopyStatusMap((prev) => ({ ...prev, [file]: "Copied" }));
      setTimeout(() => {
        setCopyStatusMap((prev) => ({ ...prev, [file]: "Copy" }));
      }, 1200);
    });
  };

  const moveToEditor = (filename, content) => {
    const newFile = {
      id: `file${Date.now()}`,
      name: filename,
      content: content || "",
      language: getFileLanguage(filename),
      languageId: null,
    };

    let existingFiles = [];
    const savedFiles = localStorage.getItem("ide_files");
    if (savedFiles) {
      try {
        existingFiles = JSON.parse(savedFiles);
      } catch (e) {
        console.error("Failed to parse saved files", e);
      }
    }

    existingFiles.push(newFile);
    localStorage.setItem("ide_files", JSON.stringify(existingFiles));
    localStorage.setItem("ide_active_file_id", newFile.id);

    navigate("/ide");
    setMessage(`File "${filename}" moved to editor.`);
  };

  const getFileLanguage = (fileName) => {
    const ext = fileName.split(".").pop().toLowerCase();
    const extensionMap = {
      py: "python",
      c: "c",
      cpp: "cpp",
      h: "cpp",
      hpp: "cpp",
      java: "java",
      js: "javascript",
      jsx: "javascript",
      ts: "typescript",
      tsx: "typescript",
      rb: "ruby",
      cs: "csharp",
      go: "go",
      rs: "rust",
      php: "php",
      html: "html",
      css: "css",
      json: "json",
      md: "markdown",
      txt: "plaintext",
    };
    return extensionMap[ext] || "plaintext";
  };

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  return (
    <div className="relative flex justify-center bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white min-h-[calc(100vh-5rem)]">
      {/* Files Container */}
      <div className="w-full max-w-4xl bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 space-y-6 overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200">
            Files
          </h2>
          {openFiles.length > 0 && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-medium border border-indigo-200 dark:border-indigo-800">
              {openFiles.length} open
            </span>
          )}
        </div>

        {/* Loading Indicator */}
        {loading && (
          <div className="flex justify-center items-center">
            <svg
              className="animate-spin h-8 w-8 text-blue-500 dark:text-blue-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-label="Loading spinner"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              ></path>
            </svg>
          </div>
        )}

        {/* Files List with Multi-Accordion */}
        <div id="accordion-collapse">
          {files.map((file, index) => {
            const isOpen = openFiles.includes(file);
            const content = fileContents[file];
            const isFileLoading = Boolean(fileLoadingMap[file]);
            const copyStatus = copyStatusMap[file] || "Copy";

            return (
              <div
                key={file}
                className="rounded-lg overflow-hidden shadow-md mb-4 border border-gray-200 dark:border-gray-700"
              >
                <h2 id={`accordion-collapse-heading-${index}`}>
                  <button
                    type="button"
                    className="flex items-center justify-between w-full p-4 font-medium text-left text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                    onClick={() => toggleFile(file)}
                    aria-expanded={isOpen}
                    aria-controls={`accordion-collapse-body-${index}`}
                  >
                    <div className="flex items-center space-x-2">
                      <FaFileAlt className="h-5 w-5 text-gray-500 dark:text-gray-300" />
                      <span>{file}</span>
                      {isFileLoading && (
                        <ArrowPathIcon className="w-4 h-4 text-blue-500 animate-spin ml-2" />
                      )}
                    </div>
                    <svg
                      className={`w-5 h-5 transition-transform duration-200 ${
                        isOpen ? "transform rotate-180" : ""
                      }`}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </h2>
                {isOpen && (
                  <div
                    id={`accordion-collapse-body-${index}`}
                    className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/80 rounded-b-lg"
                    aria-labelledby={`accordion-collapse-heading-${index}`}
                  >
                    <div className="relative">
                      {/* Button Group inside top-right of code block */}
                      <div className="absolute top-2.5 right-2.5 flex space-x-2 items-center z-10">
                        {/* Reload Individual File Button */}
                        <button
                          onClick={() => reloadSingleFile(file)}
                          disabled={isFileLoading}
                          className="px-2.5 py-1 bg-gray-200/90 dark:bg-gray-700/90 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md transition flex items-center disabled:opacity-50 text-xs shadow-xs"
                          title="Reload file from server"
                          aria-label="Reload file"
                        >
                          <ArrowPathIcon
                            className={`h-4 w-4 ${isFileLoading ? "animate-spin" : ""}`}
                          />
                        </button>

                        {/* Move to Editor Button */}
                        <button
                          onClick={() => moveToEditor(file, content)}
                          disabled={isFileLoading || content === undefined}
                          className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded-md flex items-center transition-colors disabled:opacity-50 text-xs font-medium shadow-xs"
                          aria-label="Move to editor"
                        >
                          <ArrowRightIcon className="h-4 w-4 mr-1" />
                          Move to Editor
                        </button>

                        {/* Copy Button */}
                        <button
                          onClick={() => copyToClipboard(file, content)}
                          disabled={isFileLoading || content === undefined}
                          className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded-md flex items-center transition-colors disabled:opacity-50 text-xs font-medium shadow-xs"
                          aria-label="Copy code to clipboard"
                        >
                          <ClipboardIcon className="h-4 w-4 mr-1" />
                          {copyStatus}
                        </button>
                      </div>

                      {/* File Content: starts immediately at line 1, equal margins on top & bottom */}
                      <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 p-4 pr-64 rounded-md overflow-x-auto shadow-xs">
                        {isFileLoading
                          ? "Loading content..."
                          : content !== undefined
                          ? content
                          : "Loading content..."}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Message Display */}
        {message && !loading && (
          <div className="text-center text-sm text-gray-600 dark:text-gray-400">
            {message}
          </div>
        )}
      </div>

      {/* Floating Refresh Button (Busts cache and reloads file list + open files) */}
      <button
        type="button"
        onClick={handleRefresh}
        disabled={loading}
        className="fixed top-16 right-6 md:top-20 md:right-10 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
        title="Refresh file list and reload open files"
        aria-label="Refresh files"
      >
        <ArrowPathIcon className={`h-6 w-6 ${loading ? "animate-spin" : ""}`} />
      </button>
    </div>
  );
};

export default FilesTab;
